import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import MultiValueEditableField from "../../../components/ui/formelement/v2/MultiValueEditableField";
import { ContactType, StakeholderContactDetail } from "../../../api/enrichedStakeholder";
import { getEnrichedStakeholderById } from "../logic/stakeholderSlice";
import { updateStakeholderContactDetails } from "../logic/stakeholderThunks";
import { showErrorToast } from "../../../lib/toast";

interface StakeholderContactProps {
  stakeholderId: string;
}

const StakeholderContact: React.FC<StakeholderContactProps> = ({ stakeholderId }) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const enrichedStakeholder = useAppSelector((state) =>
    getEnrichedStakeholderById(state, stakeholderId)
  );

  if (!enrichedStakeholder) return null;

  const contactDetails = enrichedStakeholder.contact_details || [];

  const getContactValues = (type: ContactType) =>
    contactDetails
      .filter((c) => c.contact_type === type)
      .map((c) => ({ id: c.contact_id, value: c.contact_value }));

  const handleSaveContact = async (
    contactType: ContactType,
    id: string | undefined,
    newValue: any
  ): Promise<boolean> => {
    if (!caseId || !newValue || String(newValue).trim() === "") return false;

    setIsSaving(true);
    try {
      const request: StakeholderContactDetail = {
        contact_id: id || uuidv4(),
        contact_type: contactType,
        contact_value: String(newValue).trim(),
      };

      await dispatch(
        updateStakeholderContactDetails({ caseId, stakeholderId, request })
      ).unwrap();

      setEditingField(null);
      return true;
    } catch {
      showErrorToast("Failed to update contact. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="Contact">
      <div className="flex w-full flex-col gap-2">
        <MultiValueEditableField
          label="Addresses"
          type="textarea"
          values={getContactValues(ContactType.ADDRESS)}
          isEditable={true}
          disabled={isSaving}
          isEditing={editingField === "addresses"}
          onEditStart={() => setEditingField("addresses")}
          onEditCancel={() => setEditingField(null)}
          onSave={(id, val) => handleSaveContact(ContactType.ADDRESS, id, val)}
        />

        <MultiValueEditableField
          label="Emails"
          type="email"
          values={getContactValues(ContactType.EMAIL)}
          isEditable={true}
          disabled={isSaving}
          isEditing={editingField === "emails"}
          onEditStart={() => setEditingField("emails")}
          onEditCancel={() => setEditingField(null)}
          onSave={(id, val) => handleSaveContact(ContactType.EMAIL, id, val)}
        />

        <MultiValueEditableField
          label="Phones"
          type="tel"
          values={getContactValues(ContactType.PHONE)}
          isEditable={true}
          disabled={isSaving}
          isEditing={editingField === "phones"}
          onEditStart={() => setEditingField("phones")}
          onEditCancel={() => setEditingField(null)}
          onSave={(id, val) => handleSaveContact(ContactType.PHONE, id, val)}
        />
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default StakeholderContact;